// biome-ignore-all lint/a11y: TODO - address a11y issues later
import { BarChart, Columns3, RotateCw, Search, X } from "lucide-react";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import type { SelectableColumn } from "@/hooks/use-column-selection";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Input } from "./ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

// New interface for selectable metrics
export interface SelectableMetric {
  id: string;
  title: string;
  tooltip?: string;
  category: string;
  categoryStyle: string;
  categoryIcon: React.ReactNode;
}

export interface EntityColumnSelectorProps {
  availableColumns: SelectableColumn[];
  isColumnSelected: (columnId: string) => boolean;
  toggleColumn: (columnId: string) => void;
  isLoading?: boolean;
  error?: Error | null;
  buttonLabel?: string;
  noColumnsMessage?: string;
  errorMessage?: string;
  loadingMessage?: string;
  // New metrics props
  availableMetrics?: SelectableMetric[];
  selectedMetrics?: string[];
  onSelectMetrics?: (metrics: string[]) => void;
  showMetricsSection?: boolean;
  defaultMetrics?: string[];
}

/**
 * Component for managing column selection for entity tables.
 * Displays a popover with checkboxes for selecting which columns to display.
 * Works with both core and custom field columns, and optionally metrics.
 */
export function EntityColumnSelector({
  availableColumns,
  isColumnSelected,
  toggleColumn,
  isLoading = false,
  error = null,
  buttonLabel = "Columns",
  noColumnsMessage = "No columns available.",
  errorMessage = "Error loading columns.",
  loadingMessage = "Loading columns...",
  // Metrics props
  availableMetrics = [],
  selectedMetrics = [],
  onSelectMetrics,
  showMetricsSection = false,
  defaultMetrics = [],
}: EntityColumnSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [localSelections, setLocalSelections] = useState<
    Record<string, boolean>
  >({});

  // Local state for metrics selection before applying
  const [localMetrics, setLocalMetrics] = useState<string[]>(selectedMetrics);

  // Initialize local selections when component opens
  // We intentionally omit availableColumns, isColumnSelected, and selectedMetrics
  // from dependencies because:
  // 1. This effect should only run when the popover opens/closes, not when the underlying data changes
  // 2. Including these would cause unnecessary re-initialization every time columns or selections change
  // 3. We want to capture the state at the moment the popover opens, then work with local state
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional - see comment above
  useEffect(() => {
    if (isOpen) {
      const initialSelections: Record<string, boolean> = {};
      availableColumns.forEach((column) => {
        initialSelections[column.id] = isColumnSelected(column.id);
      });
      setLocalSelections(initialSelections);
      setLocalMetrics(selectedMetrics);
    }
  }, [isOpen]);

  // Function to toggle local selection
  const toggleLocalSelection = (columnId: string) => {
    setLocalSelections((prev) => ({
      ...prev,
      [columnId]: !prev[columnId],
    }));
  };

  // Function to check if a column is selected locally
  const isLocallySelected = (columnId: string) => {
    return localSelections[columnId] === true;
  };

  // Function to clear local selections (local version that updates UI state)
  const clearLocalSelections = () => {
    const resetSelections: Record<string, boolean> = {};
    // Only keep required columns
    availableColumns.forEach((column) => {
      resetSelections[column.id] = !!column.required;
    });
    setLocalSelections(resetSelections);
    setLocalMetrics([]);
  };

  // Function to reset to default view (local version that updates UI state)
  const resetLocalSelectionsToDefault = () => {
    const defaultSelections: Record<string, boolean> = {};
    // Show all core columns, hide custom columns
    availableColumns.forEach((column) => {
      defaultSelections[column.id] = column.columnType === "core";
    });
    setLocalSelections(defaultSelections);

    // Set default metrics when metrics section is enabled
    if (showMetricsSection) {
      setLocalMetrics(defaultMetrics);
    } else {
      setLocalMetrics([]);
    }
  };

  // Function to apply local selections
  const applySelections = () => {
    // For each column, check if the selection state has changed
    availableColumns.forEach((column) => {
      const currentlySelected = isColumnSelected(column.id);
      const shouldBeSelected = isLocallySelected(column.id);

      // Only toggle if there's a change
      if (currentlySelected !== shouldBeSelected) {
        toggleColumn(column.id);
      }
    });

    // Apply metrics selection if metrics are enabled
    if (showMetricsSection && onSelectMetrics) {
      onSelectMetrics(localMetrics);
    }

    setIsOpen(false);
  };

  // Toggle selection of a metric locally
  const toggleMetric = (metricId: string, checked: boolean) => {
    if (checked) {
      setLocalMetrics([...localMetrics, metricId]);
    } else {
      setLocalMetrics(localMetrics.filter((m) => m !== metricId));
    }
  };

  // Filter columns based on search query
  const filteredColumns = useMemo(() => {
    if (!searchQuery) return availableColumns;

    return availableColumns.filter((column) =>
      column.title.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [availableColumns, searchQuery]);

  // Filter metrics based on search query
  const filteredMetrics = useMemo(() => {
    if (!searchQuery) return availableMetrics;

    return availableMetrics.filter(
      (metric) =>
        metric.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        metric.tooltip?.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [availableMetrics, searchQuery]);

  // Group columns by type (core, custom, and ai)
  const groupedColumns = useMemo(() => {
    const groups: Record<string, SelectableColumn[]> = {
      core: [],
      custom: [],
      ai: [],
    };

    filteredColumns.forEach((column) => {
      if (column.columnType === "core") {
        groups.core.push(column);
      } else if (column.columnType === "ai") {
        groups.ai.push(column);
      } else {
        groups.custom.push(column);
      }
    });

    return groups;
  }, [filteredColumns]);

  // Get count of all selected columns and metrics
  const selectedColumnCount = useMemo(() => {
    // When the popover is open, use the local selections which are more up to date
    if (isOpen && Object.keys(localSelections).length > 0) {
      return Object.values(localSelections).filter(Boolean).length;
    }

    // Otherwise count from the actual selected columns state
    // Count core columns from visibility state
    const visibleCoreColumnsCount = availableColumns.filter(
      (col) => col.columnType === "core" && isColumnSelected(col.id),
    ).length;

    // Count custom columns
    const visibleCustomColumnsCount = availableColumns.filter(
      (col) => col.columnType === "custom" && isColumnSelected(col.id),
    ).length;
    // Count AI columns
    const visibleAIColumnsCount = availableColumns.filter(
      (col) => col.columnType === "ai" && isColumnSelected(col.id),
    ).length;

    return (
      visibleCoreColumnsCount +
      visibleCustomColumnsCount +
      visibleAIColumnsCount
    );
  }, [localSelections, isOpen, availableColumns, isColumnSelected]);

  const selectedMetricsCount = useMemo(() => {
    return isOpen ? localMetrics.length : selectedMetrics.length;
  }, [isOpen, localMetrics, selectedMetrics]);

  const totalSelectedCount =
    selectedColumnCount + (showMetricsSection ? selectedMetricsCount : 0);

  // Handle key down for applying filter with Enter key
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      applySelections();
    }
  };

  // Determine button icon and label
  const buttonIcon =
    showMetricsSection && selectedMetricsCount > 0 ? (
      <BarChart className="h-4 w-4" />
    ) : (
      <Columns3 className="h-4 w-4" />
    );

  const displayLabel = showMetricsSection ? "Columns & Metrics" : buttonLabel;

  return (
    <div className="relative">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="flex items-center gap-1.5"
            data-testid="column-selector-button"
          >
            {buttonIcon}
            <span>{displayLabel}</span>
            {totalSelectedCount > 0 && (
              <Badge className="ml-1 bg-primary text-primary-foreground">
                {totalSelectedCount}
              </Badge>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-80 p-0"
          align="end"
          data-testid="column-selector-popover"
        >
          {isLoading ? (
            <div className="flex items-center justify-center p-8">
              <RotateCw className="h-5 w-5 animate-spin mr-2" />
              <span>{loadingMessage}</span>
            </div>
          ) : error ? (
            <div className="p-8 text-center">
              <p className="text-muted-foreground">{errorMessage}</p>
              <p className="text-xs text-muted-foreground mt-2">
                Please try again later.
              </p>
            </div>
          ) : availableColumns.length === 0 &&
            (!showMetricsSection || availableMetrics.length === 0) ? (
            <div className="p-8 text-center">
              <p className="text-muted-foreground">{noColumnsMessage}</p>
            </div>
          ) : (
            <div className="py-2" onKeyDown={handleKeyDown}>
              <div className="sticky top-0 z-10 bg-popover mb-2 mx-2">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={
                    showMetricsSection
                      ? "Search columns & metrics..."
                      : "Search columns..."
                  }
                  value={searchQuery}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setSearchQuery(e.target.value)
                  }
                  className="pl-8 pr-8"
                  data-testid="column-selector-search"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
                    aria-label="Clear search"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>

              <div className="max-h-[336px] overflow-auto pr-1">
                {filteredColumns.length === 0 &&
                (!showMetricsSection || filteredMetrics.length === 0) ? (
                  <div className="py-2 px-2 text-sm text-muted-foreground">
                    No {showMetricsSection ? "columns or metrics" : "columns"}{" "}
                    found
                  </div>
                ) : (
                  <>
                    {/* Core Columns Section */}
                    {groupedColumns.core.length > 0 && (
                      <div className="mb-3">
                        <div className="px-4 py-1">
                          <h4 className="text-xs font-semibold text-muted-foreground">
                            Standard Columns
                          </h4>
                        </div>
                        <div className="space-y-1">
                          {groupedColumns.core.map((column) => (
                            <div
                              key={column.id}
                              className={`relative flex select-none items-center rounded-sm px-4 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground group ${
                                column.required
                                  ? "cursor-not-allowed opacity-90"
                                  : "cursor-pointer"
                              }`}
                              data-testid={`column-option-${column.id}`}
                              data-selected={
                                column.required || isLocallySelected(column.id)
                                  ? "true"
                                  : "false"
                              }
                              onClick={() => {
                                if (!column.required) {
                                  toggleLocalSelection(column.id);
                                }
                              }}
                            >
                              <div
                                className={`mr-2 h-4 w-4 flex-shrink-0 rounded-sm border flex items-center justify-center self-center ${
                                  column.required
                                    ? "border-primary bg-primary/70 text-primary-foreground opacity-80" // Special styling for required columns
                                    : isLocallySelected(column.id)
                                      ? "border-primary bg-primary text-primary-foreground"
                                      : "border-primary/20"
                                }`}
                              >
                                {(isLocallySelected(column.id) ||
                                  column.required) && (
                                  <Checkbox
                                    id={`column-${column.id}`}
                                    checked={true} // Always checked for required columns
                                    disabled={column.required}
                                    data-testid={`column-checkbox-${column.id}`}
                                  />
                                )}
                              </div>
                              <div className="flex-grow min-w-0">
                                <span
                                  className={`break-words ${
                                    column.required ? "font-medium" : ""
                                  }`}
                                >
                                  {column.title}
                                  {column.required && (
                                    <span className="ml-1 text-xs text-muted-foreground">
                                      (primary)
                                    </span>
                                  )}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Metrics Section */}
                    {showMetricsSection && filteredMetrics.length > 0 && (
                      <div className="mb-3">
                        <div className="px-4 py-1">
                          <h4 className="text-xs font-semibold text-muted-foreground">
                            Metrics
                          </h4>
                        </div>
                        <div className="space-y-1">
                          {filteredMetrics.map((metric) => (
                            <div
                              key={metric.id}
                              className="relative flex select-none items-center rounded-sm px-4 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground group cursor-pointer"
                              data-testid={`metric-option-${metric.id}`}
                              data-selected={
                                localMetrics.includes(metric.id)
                                  ? "true"
                                  : "false"
                              }
                              onClick={() => {
                                toggleMetric(
                                  metric.id,
                                  !localMetrics.includes(metric.id),
                                );
                              }}
                            >
                              <div
                                className={`mr-2 h-4 w-4 flex-shrink-0 rounded-sm border flex items-center justify-center self-center ${
                                  localMetrics.includes(metric.id)
                                    ? "border-primary bg-primary text-primary-foreground"
                                    : "border-primary/20"
                                }`}
                              >
                                {localMetrics.includes(metric.id) && (
                                  <Checkbox
                                    id={`metric-${metric.id}`}
                                    checked={true}
                                    data-testid={`metric-checkbox-${metric.id}`}
                                  />
                                )}
                              </div>
                              <div className="flex-grow min-w-0">
                                <span className="break-words">
                                  {metric.title}
                                </span>
                              </div>
                              <div
                                className={`text-xs px-1.5 py-0.5 rounded-sm ${metric.categoryStyle} flex items-center gap-1 ml-2`}
                              >
                                {metric.categoryIcon}
                                {metric.category}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* AI Fields Section */}
                    {groupedColumns.ai.length > 0 && (
                      <div className="mb-3">
                        <div className="px-4 py-1">
                          <h4 className="text-xs font-semibold text-muted-foreground">
                            AI Fields
                          </h4>
                        </div>
                        <div className="space-y-1">
                          {groupedColumns.ai.map((column) => (
                            <div
                              key={column.id}
                              className={`relative flex select-none items-center rounded-sm px-4 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground group ${
                                column.required
                                  ? "cursor-not-allowed opacity-90"
                                  : "cursor-pointer"
                              }`}
                              data-testid={`column-option-${column.id}`}
                              data-selected={
                                column.required || isLocallySelected(column.id)
                                  ? "true"
                                  : "false"
                              }
                              onClick={() => {
                                if (!column.required) {
                                  toggleLocalSelection(column.id);
                                }
                              }}
                            >
                              <div
                                className={`mr-2 h-4 w-4 flex-shrink-0 rounded-sm border flex items-center justify-center self-center ${
                                  column.required
                                    ? "border-primary bg-primary/70 text-primary-foreground opacity-80"
                                    : isLocallySelected(column.id)
                                      ? "border-primary bg-primary text-primary-foreground"
                                      : "border-primary/20"
                                }`}
                              >
                                {(isLocallySelected(column.id) ||
                                  column.required) && (
                                  <Checkbox
                                    id={`column-${column.id}`}
                                    checked={
                                      column.required
                                        ? true
                                        : isLocallySelected(column.id)
                                    }
                                    disabled={column.required}
                                    data-testid={`column-checkbox-${column.id}`}
                                  />
                                )}
                              </div>
                              <div className="relative flex-grow min-w-0">
                                <span className="break-words">
                                  {column.title}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Custom Fields Section */}
                    {groupedColumns.custom.length > 0 && (
                      <div className="mb-3">
                        <div className="px-4 py-1">
                          <h4 className="text-xs font-semibold text-muted-foreground">
                            Custom Fields
                          </h4>
                        </div>
                        <div className="space-y-1">
                          {groupedColumns.custom.map((column) => (
                            <div
                              key={column.id}
                              className={`relative flex select-none items-center rounded-sm px-4 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground group ${
                                column.required
                                  ? "cursor-not-allowed opacity-90"
                                  : "cursor-pointer"
                              }`}
                              data-testid={`column-option-${column.id}`}
                              data-selected={
                                column.required || isLocallySelected(column.id)
                                  ? "true"
                                  : "false"
                              }
                              onClick={() => {
                                if (!column.required) {
                                  toggleLocalSelection(column.id);
                                }
                              }}
                            >
                              <div
                                className={`mr-2 h-4 w-4 flex-shrink-0 rounded-sm border flex items-center justify-center self-center ${
                                  column.required
                                    ? "border-primary bg-primary/70 text-primary-foreground opacity-80"
                                    : isLocallySelected(column.id)
                                      ? "border-primary bg-primary text-primary-foreground"
                                      : "border-primary/20"
                                }`}
                              >
                                {(isLocallySelected(column.id) ||
                                  column.required) && (
                                  <Checkbox
                                    id={`column-${column.id}`}
                                    checked={
                                      column.required
                                        ? true
                                        : isLocallySelected(column.id)
                                    }
                                    disabled={column.required}
                                    data-testid={`column-checkbox-${column.id}`}
                                  />
                                )}
                              </div>
                              <div className="relative flex-grow min-w-0">
                                <span className="break-words">
                                  {column.title}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              {totalSelectedCount > 0 ? (
                <div className="text-sm font-medium mt-2 h-5 px-2">
                  Selected: {selectedColumnCount}{" "}
                  {selectedColumnCount === 1 ? "column" : "columns"}
                  {showMetricsSection && selectedMetricsCount > 0 && (
                    <span>
                      , {selectedMetricsCount}{" "}
                      {selectedMetricsCount === 1 ? "metric" : "metrics"}
                    </span>
                  )}
                </div>
              ) : (
                <div className="text-sm font-medium mt-2 h-5 px-2 invisible">
                  Selected: placeholder
                </div>
              )}
            </div>
          )}

          <div className="border-t p-3 flex justify-between">
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={clearLocalSelections}
                className="text-xs px-2"
              >
                Clear
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={resetLocalSelectionsToDefault}
                className="text-xs px-2"
              >
                Default
              </Button>
            </div>
            <Button
              size="sm"
              onClick={applySelections}
              data-testid="column-selector-apply"
            >
              Apply
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
